'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { FileText, BarChart2, Star, Lightbulb, BookOpen, Download, PenLine, ChevronRight, TrendingUp, TrendingDown, Clock, Hash, CheckCircle, XCircle, Info, ArrowRight } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import BandRadarChart from './BandRadarChart';
import AnnotatedEssayTab from './AnnotatedEssayTab';
import { mockAssessment } from '@/data/mockAssessment';
import { questionBank } from '@/data/questions';
import Icon from '@/components/ui/AppIcon';


type TabId = 'annotated' | 'feedback' | 'strengths' | 'recommendations' | 'band9';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'annotated', label: 'Annotated Essay', icon: FileText },
  { id: 'feedback', label: 'Examiner Feedback', icon: BarChart2 },
  { id: 'strengths', label: 'Strengths & Weaknesses', icon: Star },
  { id: 'recommendations', label: 'Recommendations', icon: Lightbulb },
  { id: 'band9', label: 'Band 9 Answer', icon: BookOpen },
];

function getBandColor(score: number): string {
  if (score >= 8.5) return 'text-[color:var(--band-9)]';
  if (score >= 7.5) return 'text-[color:var(--band-8)]';
  if (score >= 6.5) return 'text-[color:var(--band-7)]';
  if (score >= 5.5) return 'text-[color:var(--band-6)]';
  if (score >= 4.5) return 'text-[color:var(--band-5)]';
  return 'text-danger';
}

function getBandBgClass(score: number): string {
  if (score >= 8.5) return 'score-card-9';
  if (score >= 7.5) return 'score-card-8';
  if (score >= 6.5) return 'score-card-7';
  if (score >= 5.5) return 'score-card-6';
  if (score >= 4.5) return 'score-card-5';
  return 'score-card-low';
}

function getBandLabel(score: number): string {
  if (score >= 9) return 'Expert User';
  if (score >= 8) return 'Very Good User';
  if (score >= 7) return 'Good User';
  if (score >= 6) return 'Competent User';
  if (score >= 5) return 'Modest User';
  if (score >= 4) return 'Limited User';
  return 'Extremely Limited';
}

export default function AssessmentResultsContent() {
  const [activeTab, setActiveTab] = useState<TabId>('annotated');
  const question = questionBank.find((q) => q.id === mockAssessment.questionId) || questionBank[1];

  const handleDownload = () => {
    toast.success('Report download started — check your downloads folder.');
    // BACKEND INTEGRATION: GET /api/reports/{assessmentId}/pdf
  };

  const { criteriaScores, overallBand, overallDescriptor } = mockAssessment;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
            <span>Assessment</span>
            <ChevronRight size={12} />
            <span className="text-foreground font-medium">Results — {mockAssessment.submittedAt}</span>
          </div>
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Assessment Results</h1>
          <p className="text-sm text-muted-foreground mt-1">
            AI examiner feedback calibrated to official IELTS Writing Task 2 band descriptors.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            href="/"
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-secondary-foreground bg-secondary rounded-lg hover:bg-muted border transition-all duration-150 active:scale-95"
          >
            <PenLine size={14} />
            Try Another Essay
          </Link>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 shadow-sm transition-all duration-150 active:scale-95"
          >
            <Download size={14} />
            Download Report
          </button>
        </div>
      </div>

      {/* Question Summary */}
      <div className="mb-6 p-4 rounded-xl border bg-card shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
            <FileText size={15} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <Badge variant="info">{question.type}</Badge>
              <Badge variant="secondary">{question.topic}</Badge>
              <Badge variant="warning">{question.difficulty}</Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock size={11} />
                {mockAssessment.submittedAt}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Hash size={11} />
                {mockAssessment.wordCount} words
              </span>
            </div>
            <p className="text-sm text-foreground leading-relaxed line-clamp-2">{question.prompt}</p>
          </div>
        </div>
      </div>

      {/* Score Overview Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4 mb-6">
        {/* Hero — Overall Band Score */}
        <div className="xl:col-span-1 bg-primary rounded-2xl p-6 text-primary-foreground shadow-md flex flex-col justify-between min-h-[160px]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest opacity-80">Overall Band Score</p>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="band-score-display text-6xl leading-none">{overallBand.toFixed(1)}</span>
              <span className="text-lg font-semibold opacity-80">/ 9.0</span>
            </div>
          </div>
          <div>
            <p className="text-base font-bold mt-3">{overallDescriptor}</p>
            <p className="text-xs opacity-70 mt-1">
              Rounded per IELTS convention from criterion average {((criteriaScores.reduce((a, b) => a + b.score, 0) / 4)).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Criterion Score Cards */}
        {criteriaScores.map((c) => (
          <div
            key={`score-card-${c.code}`}
            className={`bg-card rounded-xl border p-5 shadow-sm ${getBandBgClass(c.score)} flex flex-col justify-between`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider bg-muted px-2 py-0.5 rounded">
                    {c.code}
                  </span>
                </div>
                <p className="text-sm font-semibold text-foreground">{c.criterion}</p>
              </div>
              <div className="text-right">
                <span className={`band-score-display text-3xl ${getBandColor(c.score)}`}>
                  {c.score.toFixed(1)}
                </span>
                <p className="text-xs text-muted-foreground">{getBandLabel(c.score)}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{c.descriptor}</p>
          </div>
        ))}

        {/* Radar Chart Card */}
        <div className="md:col-span-2 xl:col-span-3 2xl:col-span-3 bg-card rounded-xl border p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-foreground">Criterion Profile</p>
              <p className="text-xs text-muted-foreground mt-0.5">Band scores across all four assessment criteria</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 rounded-full bg-primary" />
                <span className="text-xs text-muted-foreground">Your Score</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 rounded-full bg-muted-foreground/40" style={{ borderStyle: 'dashed' }} />
                <span className="text-xs text-muted-foreground">Band 7 Target</span>
              </div>
            </div>
          </div>
          <BandRadarChart scores={criteriaScores} />
        </div>
      </div>

      {/* Tabbed Content */}
      <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
        {/* Tab Bar */}
        <div className="flex overflow-x-auto border-b bg-muted/20">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-all duration-150 shrink-0 ${
                  isActive
                    ? 'text-primary bg-card tab-underline-active' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="fade-in">
          {activeTab === 'annotated' && <AnnotatedEssayTab />}
          {activeTab === 'feedback' && <ExaminerFeedbackTab />}
          {activeTab === 'strengths' && <StrengthsWeaknessesTab />}
          {activeTab === 'recommendations' && <RecommendationsTab />}
          {activeTab === 'band9' && <Band9AnswerTab />}
        </div>
      </div>
    </div>
  );
}

// ─── Examiner Feedback Tab ───────────────────────────────────────────────────

function ExaminerFeedbackTab() {
  const { criteriaScores } = mockAssessment;
  const [expanded, setExpanded] = useState<string | null>('TR');

  return (
    <div className="p-6 space-y-3">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-foreground">Overall Performance Summary</h3>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          This response demonstrates a competent but inconsistent performance across the four assessment criteria. The candidate shows a clear understanding of the task and presents a recognisable argument structure; however, the overall score is constrained principally by a limited lexical range (Band 5.5) and uneven task coverage. The strongest performance is in Grammatical Range and Accuracy (Band 6.5), where a reasonable variety of sentence structures is evident. To reach Band 7.0, the candidate must prioritise expanding topic-specific vocabulary and ensuring all parts of the task are developed with equal depth and supporting evidence.
        </p>
      </div>

      <div className="space-y-2">
        {criteriaScores.map((c) => (
          <div key={`feedback-${c.code}`} className="border rounded-xl overflow-hidden">
            <button
              onClick={() => setExpanded(expanded === c.code ? null : c.code)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-all duration-150"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-primary">{c.code}</span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">{c.criterion}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.descriptor}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`band-score-display text-2xl font-bold ${getBandColor(c.score)}`}>
                  {c.score.toFixed(1)}
                </span>
                <ChevronRight
                  size={16}
                  className={`text-muted-foreground transition-transform duration-200 ${expanded === c.code ? 'rotate-90' : ''}`}
                />
              </div>
            </button>
            {expanded === c.code && (
              <div className="px-5 pb-5 pt-1 border-t bg-muted/10 fade-in">
                <p className="text-sm text-secondary-foreground leading-relaxed">{c.commentary}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Strengths & Weaknesses Tab ──────────────────────────────────────────────

function StrengthsWeaknessesTab() {
  const { strengths, weaknesses } = mockAssessment;

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strengths */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-success-bg border border-success-border flex items-center justify-center">
              <CheckCircle size={14} className="text-success" />
            </div>
            <h3 className="text-sm font-semibold text-foreground">Strengths</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-success-bg text-success font-semibold border border-success-border">
              {strengths.length}
            </span>
          </div>
          <div className="space-y-2.5">
            {strengths.map((s, i) => (
              <div key={`strength-${i + 1}`} className="flex items-start gap-3 p-3.5 rounded-lg bg-success-bg border border-success-border">
                <CheckCircle size={14} className="text-success shrink-0 mt-0.5" />
                <p className="text-sm text-secondary-foreground leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Weaknesses */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-danger-bg border border-danger-border flex items-center justify-center">
              <XCircle size={14} className="text-danger" />
            </div>
            <h3 className="text-sm font-semibold text-foreground">Weaknesses</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-danger-bg text-danger font-semibold border border-danger-border">
              {weaknesses.length}
            </span>
          </div>
          <div className="space-y-2.5">
            {weaknesses.map((w, i) => (
              <div key={`weakness-${i + 1}`} className="flex items-start gap-3 p-3.5 rounded-lg bg-danger-bg border border-danger-border">
                <XCircle size={14} className="text-danger shrink-0 mt-0.5" />
                <p className="text-sm text-secondary-foreground leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Score comparison */}
      <div className="mt-6 p-4 rounded-xl bg-info-bg border border-info-border">
        <div className="flex items-center gap-2 mb-3">
          <Info size={14} className="text-info" />
          <span className="text-sm font-semibold text-info">Score Gap Analysis — Path to Band 7.0</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {mockAssessment.criteriaScores.map((c) => {
            const gap = 7.0 - c.score;
            const isAbove = gap <= 0;
            return (
              <div key={`gap-${c.code}`} className="text-center p-3 rounded-lg bg-card border">
                <p className="text-xs font-semibold text-muted-foreground mb-1">{c.code}</p>
                <p className={`band-score-display text-xl font-bold ${getBandColor(c.score)}`}>{c.score.toFixed(1)}</p>
                <div className={`flex items-center justify-center gap-1 mt-1 text-xs font-semibold ${isAbove ? 'text-success' : 'text-warning'}`}>
                  {isAbove ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                  {isAbove ? `+${Math.abs(gap).toFixed(1)} above` : `${gap.toFixed(1)} to go`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Recommendations Tab ─────────────────────────────────────────────────────

function RecommendationsTab() {
  const { recommendations } = mockAssessment;

  const priorityConfig = {
    High: { color: 'text-danger', bg: 'bg-danger-bg', border: 'border-danger-border', dot: 'bg-danger' },
    Medium: { color: 'text-warning', bg: 'bg-warning-bg', border: 'border-warning-border', dot: 'bg-warning' },
    Low: { color: 'text-info', bg: 'bg-info-bg', border: 'border-info-border', dot: 'bg-info' },
  };

  const grouped = {
    High: recommendations.filter((r) => r.priority === 'High'),
    Medium: recommendations.filter((r) => r.priority === 'Medium'),
    Low: recommendations.filter((r) => r.priority === 'Low'),
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/15">
        <Lightbulb size={16} className="text-primary shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-primary">Prioritised Action Plan — Band 6.0 → 7.0</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            The following recommendations are ordered by impact. Addressing High priority items first will produce the fastest improvement in your overall band score. Focus on one or two items per practice session rather than attempting all simultaneously.
          </p>
        </div>
      </div>

      {(['High', 'Medium', 'Low'] as const).map((priority) => {
        const items = grouped[priority];
        if (items.length === 0) return null;
        const cfg = priorityConfig[priority];
        return (
          <div key={`priority-group-${priority}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
              <h3 className={`text-sm font-bold ${cfg.color}`}>{priority} Priority</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color} font-semibold border ${cfg.border}`}>
                {items.length} action{items.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-3">
              {items.map((rec) => (
                <div key={rec.id} className={`p-4 rounded-xl border ${cfg.bg} ${cfg.border}`}>
                  <div className="flex items-start gap-3">
                    <ArrowRight size={14} className={`${cfg.color} shrink-0 mt-0.5`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <p className="text-sm font-semibold text-foreground">{rec.action}</p>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                          {rec.criterion}
                        </span>
                      </div>
                      <p className="text-sm text-secondary-foreground leading-relaxed">{rec.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Band 9 Answer Tab ───────────────────────────────────────────────────────

function Band9AnswerTab() {
  const { band9Outline, band9Essay } = mockAssessment;
  const [showOutline, setShowOutline] = useState(true);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Band 9 Model Answer</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Examiner-generated model response demonstrating Band 9 performance standards.
          </p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted">
          <button
            onClick={() => setShowOutline(true)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-150 ${
              showOutline ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Outline
          </button>
          <button
            onClick={() => setShowOutline(false)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-150 ${
              !showOutline ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Full Essay
          </button>
        </div>
      </div>

      {showOutline ? (
        <div className="space-y-4 fade-in">
          {/* Essay type + thesis */}
          <div className="p-4 rounded-xl bg-primary/5 border border-primary/15">
            <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Essay Type</p>
            <p className="text-sm font-semibold text-foreground">{band9Outline.essayType}</p>
          </div>

          <div className="p-4 rounded-xl bg-card border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Thesis Statement</p>
            <p className="text-sm text-foreground leading-relaxed font-medium italic">"{band9Outline.thesisStatement}"</p>
          </div>

          <div className="p-4 rounded-xl bg-card border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Writer's Position</p>
            <p className="text-sm text-foreground leading-relaxed">{band9Outline.position}</p>
          </div>

          {/* Paragraph outlines */}
          <div className="space-y-3">
            {[
              { label: 'Introduction', content: band9Outline.introduction, color: 'bg-info-bg border-info-border' },
            ].map((p) => (
              <div key={`outline-intro`} className={`p-4 rounded-xl border ${p.color}`}>
                <p className="text-xs font-bold text-info uppercase tracking-wider mb-2">{p.label}</p>
                <p className="text-sm text-secondary-foreground leading-relaxed">{p.content}</p>
              </div>
            ))}

            {band9Outline.bodyParagraphs.map((bp, i) => (
              <div key={bp.id} className="p-4 rounded-xl border bg-card">
                <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Body Paragraph {i + 1}</p>
                <p className="text-sm font-semibold text-foreground mb-2">{bp.mainIdea}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{bp.support}</p>
              </div>
            ))}

            <div className="p-4 rounded-xl border bg-success-bg border-success-border">
              <p className="text-xs font-bold text-success uppercase tracking-wider mb-2">Conclusion</p>
              <p className="text-sm text-secondary-foreground leading-relaxed">{band9Outline.conclusion}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="fade-in">
          {/* Band 9 badge */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success-bg border border-success-border">
              <Star size={13} className="text-success" />
              <span className="text-xs font-bold text-success">Band 9.0 — Expert User</span>
            </div>
            <span className="text-xs text-muted-foreground">
              {countWords(band9Essay)} words
            </span>
          </div>

          {/* Essay text */}
          <div className="p-6 rounded-xl border bg-muted/10">
            {band9Essay.split('\n\n').map((para, i) => (
              <p
                key={`band9-para-${i + 1}`}
                className="text-sm text-foreground leading-[1.85] mb-4 last:mb-0 essay-textarea"
              >
                {para}
              </p>
            ))}
          </div>

          {/* Annotation legend */}
          <div className="mt-4 p-4 rounded-xl bg-info-bg border border-info-border">
            <div className="flex items-center gap-2 mb-2">
              <Info size={13} className="text-info" />
              <span className="text-xs font-semibold text-info">What makes this Band 9?</span>
            </div>
            <ul className="space-y-1.5">
              {[
                'All parts of the task fully addressed with a consistent, clearly developed position throughout.',
                'Sophisticated critical thinking — Norway vs. UK recidivism data cited as specific, verifiable evidence.',
                'Advanced vocabulary used naturally: recidivism, custodial terms, incapacitation, rehabilitative, proportionate.',
                'Wide range of grammatical structures with near-native accuracy — complex sentences, passives, conditionals.',
                'Cohesive devices are varied and natural: "By contrast," "This stark disparity indicates," "It follows that."',
                'Conclusion synthesises the argument rather than merely restating the introduction.',
              ].map((point, i) => (
                <li key={`band9-point-${i + 1}`} className="flex items-start gap-2 text-xs text-secondary-foreground">
                  <CheckCircle size={12} className="text-success shrink-0 mt-0.5" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function countWords(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).filter(Boolean).length;
}

function ChevronRight({ size, className }: { size: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}